import { FilteredItemProps } from '@delmonicos/shared-design-system-react/src/components/filtered-item/filtered-item';
import { GetTokensPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/tokens/models/inputs/get-tokens-paginated.model';
import { GatewayTokenModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/tokens/models/outputs/gateway-token.model';
import {
  ColumnDef,
  ColumnOrderState,
  VisibilityState,
  createColumnHelper,
} from '@tanstack/react-table';

import { TokenIsValidFilterStateController } from '../token-is-valid-filter-state/token-is-valid-filter-state.controller';
import { TokenTypeFilterStateController } from '../token-type-filter-state/token-type-filter-state.controller';
import { TokenListCommentColumn } from './token-list-comment.column';
import { TokenListContractColumn } from './token-list-contract.column';
import { TokenListFullNameColumn } from './token-list-full-name.column';
import { TokenListIssuerColumn } from './token-list-issuer.column';
import { TokenListUidColumn } from './token-list-uid.column';
import { TokenListValidColumn } from './token-list-valid.column';
import { TokenListVisualNumberColumn } from './token-list-visual-number.column';

// /////////////////////////////////////////////////////////////////////////////
// SORTING
// /////////////////////////////////////////////////////////////////////////////

export const TOKEN_LIST_SORT_FIELD: NonNullable<
  GetTokensPaginatedModel['sortField']
> = 'createdAt';

export const TOKEN_LIST_SORT_ORDER: NonNullable<
  GetTokensPaginatedModel['sortOrder']
> = 1;

// /////////////////////////////////////////////////////////////////////////////
// FILTERING
// /////////////////////////////////////////////////////////////////////////////

type ResourceListFilter = Partial<
  Record<
    keyof GetTokensPaginatedModel,
    {
      componentType: React.ComponentType<{
        value: any;
        onRemove: FilteredItemProps['onRemove'];
      }>;
    }
  >
>;

export const TOKEN_LIST_FILTER: ResourceListFilter = {
  valid: {
    componentType: TokenIsValidFilterStateController,
  },
  type: {
    componentType: TokenTypeFilterStateController,
  },
} as const;

export const TOKEN_LIST_FILTER_NAME_LIST = Object.keys(
  TOKEN_LIST_FILTER,
) as Array<keyof typeof TOKEN_LIST_FILTER>;

// /////////////////////////////////////////////////////////////////////////////
// DISPLAYING
// /////////////////////////////////////////////////////////////////////////////

export const tokenListColumnHelper = createColumnHelper<GatewayTokenModel>();

type ResourceColumnType = ColumnDef<GatewayTokenModel, any>[];

export const TOKEN_LIST_COLUMN_LIST: ResourceColumnType = [
  tokenListColumnHelper.accessor('visualNumber', TokenListVisualNumberColumn),
  tokenListColumnHelper.accessor('uid', TokenListUidColumn),
  tokenListColumnHelper.accessor('uid', TokenListContractColumn),
  tokenListColumnHelper.accessor('firstName', TokenListFullNameColumn),
  tokenListColumnHelper.accessor('issuer', TokenListIssuerColumn),
  tokenListColumnHelper.accessor('comment', TokenListCommentColumn),
  tokenListColumnHelper.accessor('valid', TokenListValidColumn),
];

export const TOKEN_LIST_COLUMN_ID_LIST = TOKEN_LIST_COLUMN_LIST.flatMap(
  (columnDef) => {
    if ('accessorKey' in columnDef) {
      return [columnDef.id ?? (columnDef.accessorKey as string)];
    }

    if (columnDef.id) {
      return [columnDef.id];
    }

    return [];
  },
);

export const TOKEN_LIST_COLUMN_ORDER = [...TOKEN_LIST_COLUMN_ID_LIST];

export const TOKEN_LIST_COLUMN_VISIBILITY: VisibilityState =
  TOKEN_LIST_COLUMN_ID_LIST.reduce(
    (columnVisibility, columnId) => ({
      ...columnVisibility,
      [columnId]: true,
    }),
    {} as VisibilityState,
  );

// /////////////////////////////////////////////////////////////////////////////
// OPTIONS
// /////////////////////////////////////////////////////////////////////////////

export interface TokenListOptionsModel {
  columnVisibility: VisibilityState;
  columnOrder: ColumnOrderState;
}

export const TOKEN_LIST_DEFAULT_OPTIONS: TokenListOptionsModel = {
  columnVisibility: TOKEN_LIST_COLUMN_VISIBILITY,
  columnOrder: TOKEN_LIST_COLUMN_ORDER,
};

export const TOKEN_LIST_COLUMN_LABEL_I18N_KEY: Record<string, string> = {
  visualNumber: 'delmonicos-shared-sdk.outputs-token.visual-number',
  uid: 'delmonicos-shared-sdk.outputs-token.uid',
  contract: 'delmonicos-shared-sdk.outputs-token-alt.contract-number',
  firstName: 'delmonicos-shared-sdk.outputs-token.first-name',
  issuer: 'delmonicos-shared-sdk.outputs-token.issuer',
  comment: 'delmonicos-shared-sdk.outputs-token.comment',
  valid: 'delmonicos-shared-sdk.outputs-token.valid',
};
